import {useCallback, useEffect, useRef} from 'react';

import {beginCriticalWorkflowPainWindow} from '../../../util/datadog/criticalWorkflows/painWindow/observer';
import type {CriticalWorkflowExplicitObservationHandle} from '../../../util/datadog/criticalWorkflows/painWindow/types';
import {recordRumDurationVital} from '../../../util/datadog/datadogRum';

export type LinePlotRenderer = 'react-vis' | 'uplot';

export interface LinePlotHoverMetricContext {
  metricCount: number;
  metricName?: string;
}

interface LinePlotMetricLine {
  aux?: boolean;
  metricName?: string;
}

type LinePlotHoverEntry = {
  renderer: LinePlotRenderer;
  metricContext: LinePlotHoverMetricContext;
  startedAt: number;
  painObservation?: CriticalWorkflowExplicitObservationHandle;
  timeoutId: number;
  handlePageHide: () => void;
  handleVisibilityChange: () => void;
};

export const LINE_PLOT_HOVER_MAX_DURATION_MS = 10_000;

const LINE_PLOT_HOVER_DURATION_VITAL = 'line_plot_hover_duration';
const LINE_PLOT_HOVER_DESCRIPTION =
  'pointer residence on a runs line plot crosshair surface';
const LINE_PLOT_HOVER_WORKFLOW = 'line-plot-hovered';

export function getLinePlotHoverMetricContext(
  lines: readonly LinePlotMetricLine[]
): LinePlotHoverMetricContext {
  const metricNames = new Set<string>();
  for (const line of lines) {
    if (!line.aux && line.metricName != null) {
      metricNames.add(line.metricName);
    }
  }

  if (metricNames.size !== 1) {
    return {metricCount: metricNames.size};
  }
  return {metricCount: 1, metricName: metricNames.values().next().value};
}

export function useLinePlotHoverSession(
  renderer: LinePlotRenderer,
  metricContext: LinePlotHoverMetricContext
): {onMouseEnter: () => void; onMouseLeave: () => void} {
  const hoverEntryRef = useRef<LinePlotHoverEntry | undefined>(undefined);
  const rendererRef = useRef(renderer);

  const clearEntryResources = useCallback((entry: LinePlotHoverEntry) => {
    window.clearTimeout(entry.timeoutId);
    document.removeEventListener(
      'visibilitychange',
      entry.handleVisibilityChange
    );
    window.removeEventListener('pagehide', entry.handlePageHide);
  }, []);

  const dropEntry = useCallback(
    (entry: LinePlotHoverEntry): boolean => {
      if (hoverEntryRef.current !== entry) {
        return false;
      }
      hoverEntryRef.current = undefined;
      clearEntryResources(entry);
      return true;
    },
    [clearEntryResources]
  );

  const cancelEntry = useCallback(
    (entry: LinePlotHoverEntry) => {
      const dropped = dropEntry(entry);
      if (dropped) {
        entry.painObservation?.cancel();
      }
    },
    [dropEntry]
  );

  const cancelCurrentEntry = useCallback(() => {
    const entry = hoverEntryRef.current;
    if (entry != null) {
      cancelEntry(entry);
    }
  }, [cancelEntry]);

  const onMouseEnter = useCallback(() => {
    cancelCurrentEntry();
    const linePlotContext = {
      renderer,
      metricCount: metricContext.metricCount,
      ...(metricContext.metricName == null
        ? {}
        : {metricName: metricContext.metricName}),
    };
    const entry: LinePlotHoverEntry = {
      renderer,
      metricContext: {...metricContext},
      startedAt: performance.now(),
      painObservation:
        renderer === 'uplot'
          ? beginCriticalWorkflowPainWindow({
              workflow: LINE_PLOT_HOVER_WORKFLOW,
              workflowContext: linePlotContext,
              maxDurationMs: LINE_PLOT_HOVER_MAX_DURATION_MS,
            })
          : undefined,
      timeoutId: 0,
      handlePageHide: () => {},
      handleVisibilityChange: () => {},
    };
    // The pain monitor owns page abandonment and reports the session as
    // abandoned. Only the residence duration is dropped here, so the pain
    // handle must not be canceled on these paths.
    entry.handlePageHide = () => dropEntry(entry);
    entry.handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        dropEntry(entry);
      }
    };
    entry.timeoutId = window.setTimeout(
      () => cancelEntry(entry),
      LINE_PLOT_HOVER_MAX_DURATION_MS
    );
    hoverEntryRef.current = entry;
    document.addEventListener('visibilitychange', entry.handleVisibilityChange);
    window.addEventListener('pagehide', entry.handlePageHide);
  }, [cancelCurrentEntry, cancelEntry, dropEntry, metricContext, renderer]);

  const onMouseLeave = useCallback(() => {
    const entry = hoverEntryRef.current;
    hoverEntryRef.current = undefined;
    if (entry == null) {
      return;
    }
    clearEntryResources(entry);

    const duration = performance.now() - entry.startedAt;
    if (
      !Number.isFinite(duration) ||
      duration < 0 ||
      duration >= LINE_PLOT_HOVER_MAX_DURATION_MS
    ) {
      entry.painObservation?.cancel();
      return;
    }

    entry.painObservation?.complete();
    recordRumDurationVital(LINE_PLOT_HOVER_DURATION_VITAL, {
      context: {
        linePlot: {
          renderer: entry.renderer,
          metricCount: entry.metricContext.metricCount,
          ...(entry.metricContext.metricName == null
            ? {}
            : {metricName: entry.metricContext.metricName}),
        },
      },
      description: LINE_PLOT_HOVER_DESCRIPTION,
      duration,
      startTime: performance.timeOrigin + entry.startedAt,
    });
  }, [clearEntryResources]);

  useEffect(() => {
    if (rendererRef.current !== renderer) {
      cancelCurrentEntry();
      rendererRef.current = renderer;
    }
  }, [cancelCurrentEntry, renderer]);

  useEffect(() => cancelCurrentEntry, [cancelCurrentEntry]);

  return {onMouseEnter, onMouseLeave};
}
